import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import React from "react";

interface DocumentCountBadgeProps {
  /** Number of documents in the folder */
  count: number;
  /** Badge variant style */
  variant?: "secondary" | "outline";
  /** Additional CSS classes */
  className?: string;
}

/**
 * DocumentCountBadge - Atom component
 *
 * Displays document count in folders with proper formatting.
 * Automatically hidden when count is 0.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <DocumentCountBadge count={5} />
 * <DocumentCountBadge count={42} variant="outline" />
 * <DocumentCountBadge count={150} /> // Shows "150"
 * ```
 */
export const DocumentCountBadge = React.memo(function DocumentCountBadge({
  count,
  variant = "secondary",
  className,
}: DocumentCountBadgeProps) {
  if (count === 0) {
    return null;
  }

  const displayCount = count.toString();
  const ariaLabel = `${count} ${count === 1 ? "document" : "documents"}`;

  return (
    <Badge
      variant={variant}
      className={cn(
        "text-xs font-semibold",
        "border border-slate-200/50 bg-slate-100 text-slate-700",
        "group-hover:border-slate-300/50 group-hover:bg-slate-200",
        "transition-all duration-200",
        "shadow-subtle",
        className
      )}
      aria-label={ariaLabel}
    >
      {displayCount}
    </Badge>
  );
});
